import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useStakeholderGroups,
  useCreateStakeholderGroup,
  useUpdateStakeholderGroup,
  useDeleteStakeholderGroup
} from '../../hooks/useStakeholderGroups';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { UserGroupIcon, PencilIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { useUI } from '../../contexts/UIContext';

/**
 * Stakeholder Groups Management Page
 * Allows users to create, view, edit, and delete stakeholder groups
 */
const StakeholderGroupsPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useUI();
  const { data: groups, isLoading, error, refetch } = useStakeholderGroups();
  const createGroup = useCreateStakeholderGroup();
  const updateGroup = useUpdateStakeholderGroup();
  const deleteGroup = useDeleteStakeholderGroup();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [formData, setFormData] = useState({
    group_name: '',
    group_type: 'team',
    description: ''
  });

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');

  // Filter groups based on search query and selected type
  const filteredGroups = useMemo(() => {
    if (!groups) return [];

    return groups.filter(group => {
      const matchesSearch = searchQuery === '' ||
        group.group_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (group.description && group.description.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesType = selectedType === 'all' || group.group_type === selectedType;

      return matchesSearch && matchesType;
    });
  }, [groups, searchQuery, selectedType]);

  const handleCreateGroup = async (e) => {
    e.preventDefault();

    try {
      await createGroup.mutateAsync({
        group_name: formData.group_name,
        group_type: formData.group_type,
        description: formData.description
      });

      setFormData({ group_name: '', group_type: 'team', description: '' });
      setShowCreateModal(false);
      showNotification('Stakeholder group created successfully', 'success');
      refetch();
    } catch (error) {
      console.error('Failed to create stakeholder group:', error);
      showNotification(`Failed to create stakeholder group: ${error.message}`, 'error');
    }
  };

  const handleEditGroup = async (e) => {
    e.preventDefault();

    try {
      await updateGroup.mutateAsync({
        groupId: editingGroup.id,
        updates: {
          description: formData.description
        }
      });

      setFormData({ group_name: '', group_type: 'team', description: '' });
      setShowEditModal(false);
      setEditingGroup(null);
      showNotification('Stakeholder group updated successfully', 'success');
      refetch();
    } catch (error) {
      console.error('Failed to update stakeholder group:', error);
      showNotification(`Failed to update stakeholder group: ${error.message}`, 'error');
    }
  };

  const handleDeleteClick = (group) => {
    setDeletingGroup(group);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingGroup) return;

    try {
      await deleteGroup.mutateAsync(deletingGroup.id);
      showNotification('Stakeholder group deleted successfully', 'success');
      refetch();
    } catch (error) {
      console.error('Failed to delete stakeholder group:', error);
      showNotification(`Failed to delete stakeholder group: ${error.message}`, 'error');
    } finally {
      setDeletingGroup(null);
    }
  };

  const openEditModal = (group) => {
    setEditingGroup(group);
    setFormData({
      group_name: group.group_name,
      group_type: group.group_type,
      description: group.description || ''
    });
    setShowEditModal(true);
  };

  const openCreateModal = () => {
    setFormData({ group_name: '', group_type: 'team', description: '' });
    setShowCreateModal(true);
  };

  const viewGroupDetail = (groupId) => {
    navigate(`/admin/stakeholder-groups/${groupId}`);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Error loading stakeholder groups: {error.message}</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <p className="text-secondary-600">
          Manage stakeholder groups to organize teams and departments that consume your data.
          Assign users to groups and connect groups to data objects for impact tracking and notifications.
        </p>
      </div>

      {/* Search and Filter Bar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full sm:w-auto">
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-secondary-400" />
            </div>
            <input
              type="text"
              className="input input-bordered bg-white text-secondary-900 w-full pl-10"
              placeholder="Search groups by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-secondary-500" />
            <select
              className="select select-bordered bg-white text-secondary-900"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="team">Team</option>
              <option value="department">Department</option>
              <option value="business_unit">Business Unit</option>
              <option value="external">External</option>
            </select>
          </div>
        </div>

        {/* Create Button */}
        <button
          onClick={openCreateModal}
          className="btn btn-primary whitespace-nowrap"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Stakeholder Group
        </button>
      </div>

      {/* Results Summary */}
      {groups && groups.length > 0 && (
        <div className="mb-4 text-sm text-secondary-600">
          Showing {filteredGroups.length} of {groups.length} stakeholder group{groups.length === 1 ? '' : 's'}
          {(searchQuery || selectedType !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedType('all');
              }}
              className="ml-2 text-primary hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Stakeholder Groups List */}
      <div className="bg-white shadow-xl rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table w-full">
            <thead className="bg-secondary-50">
              <tr>
                <th className="text-secondary-900">Name</th>
                <th className="text-secondary-900">Type</th>
                <th className="text-secondary-900">Description</th>
                <th className="text-secondary-900">Members</th>
                <th className="text-secondary-900">Data Objects</th>
                <th className="text-secondary-900">Created</th>
                <th className="text-secondary-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!groups || groups.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-secondary-500">
                    <UserGroupIcon className="h-12 w-12 mx-auto mb-3 text-secondary-300" />
                    <p className="text-lg mb-2">No stakeholder groups yet</p>
                    <p className="text-sm">Create your first stakeholder group to organize your data consumers</p>
                  </td>
                </tr>
              ) : filteredGroups.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-secondary-500">
                    <MagnifyingGlassIcon className="h-12 w-12 mx-auto mb-3 text-secondary-300" />
                    <p className="text-lg mb-2">No groups match your search</p>
                    <p className="text-sm">Try adjusting your search or filters</p>
                  </td>
                </tr>
              ) : (
                filteredGroups.map((group) => (
                  <tr
                    key={group.id}
                    className="hover:bg-secondary-50 cursor-pointer"
                    onClick={() => viewGroupDetail(group.id)}
                  >
                    <td className="font-medium text-secondary-900">
                      <div className="flex items-center">
                        <UserGroupIcon className="h-5 w-5 mr-2 text-secondary-400" />
                        {group.group_name}
                      </div>
                    </td>
                    <td>
                      <span className="badge badge-sm badge-secondary capitalize">
                        {group.group_type}
                      </span>
                    </td>
                    <td className="text-secondary-700 max-w-md truncate">
                      {group.description || <span className="text-secondary-400 italic">No description</span>}
                    </td>
                    <td className="text-secondary-700">
                      {group.member_count || 0} {group.member_count === 1 ? 'member' : 'members'}
                    </td>
                    <td className="text-secondary-700">
                      {group.object_count || 0} {group.object_count === 1 ? 'object' : 'objects'}
                    </td>
                    <td className="text-secondary-600 text-sm">
                      {new Date(group.created_at).toLocaleDateString()}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEditModal(group)}
                          className="btn btn-ghost btn-sm text-primary hover:bg-primary hover:text-white"
                          title="Edit group"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(group)}
                          className="btn btn-ghost btn-sm text-error hover:bg-error hover:text-white"
                          title="Delete group"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">What are Stakeholder Groups?</h3>
        <p className="text-blue-800 text-sm mb-2">
          Stakeholder groups represent teams, departments, or user groups that consume your data.
          Use them to:
        </p>
        <ul className="list-disc list-inside space-y-1 text-sm text-blue-700 ml-2">
          <li>Track which teams depend on specific data objects</li>
          <li>Notify relevant users when data issues occur</li>
          <li>Measure business impact when data quality degrades</li>
          <li>Organize users for better collaboration</li>
        </ul>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal modal-open">
          <div className="modal-box bg-white">
            <h3 className="font-bold text-lg text-secondary-900 mb-4">Create Stakeholder Group</h3>
            <form onSubmit={handleCreateGroup}>
              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text text-secondary-900">Group Name *</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered bg-white text-secondary-900"
                  placeholder="e.g., Sales Team, Marketing Analytics"
                  value={formData.group_name}
                  onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                  required
                />
                <label className="label">
                  <span className="label-text-alt text-secondary-600">
                    Choose a descriptive name for this stakeholder group
                  </span>
                </label>
              </div>

              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text text-secondary-900">Group Type *</span>
                </label>
                <select
                  className="select select-bordered bg-white text-secondary-900"
                  value={formData.group_type}
                  onChange={(e) => setFormData({ ...formData, group_type: e.target.value })}
                  required
                >
                  <option value="team">Team</option>
                  <option value="department">Department</option>
                  <option value="business_unit">Business Unit</option>
                  <option value="external">External</option>
                </select>
                <label className="label">
                  <span className="label-text-alt text-secondary-600">
                    Select the type that best describes this group
                  </span>
                </label>
              </div>

              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text text-secondary-900">Description</span>
                </label>
                <textarea
                  className="textarea textarea-bordered bg-white text-secondary-900"
                  placeholder="Describe the purpose and scope of this stakeholder group"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="modal-action">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-ghost"
                  disabled={createGroup.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createGroup.isPending}
                >
                  {createGroup.isPending ? 'Creating...' : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingGroup && (
        <div className="modal modal-open">
          <div className="modal-box bg-white">
            <h3 className="font-bold text-lg text-secondary-900 mb-4">Edit Stakeholder Group</h3>
            <form onSubmit={handleEditGroup}>
              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text text-secondary-900">Group Name</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered bg-secondary-100 text-secondary-500"
                  value={formData.group_name}
                  disabled
                />
                <label className="label">
                  <span className="label-text-alt text-secondary-600">
                    Group name cannot be changed
                  </span>
                </label>
              </div>

              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text text-secondary-900">Group Type</span>
                </label>
                <select
                  className="select select-bordered bg-secondary-100 text-secondary-500"
                  value={formData.group_type}
                  disabled
                >
                  <option value="team">Team</option>
                  <option value="department">Department</option>
                  <option value="business_unit">Business Unit</option>
                  <option value="external">External</option>
                </select>
                <label className="label">
                  <span className="label-text-alt text-secondary-600">
                    Group type cannot be changed
                  </span>
                </label>
              </div>

              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text text-secondary-900">Description</span>
                </label>
                <textarea
                  className="textarea textarea-bordered bg-white text-secondary-900"
                  placeholder="Describe the purpose and scope of this stakeholder group"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="modal-action">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingGroup(null);
                  }}
                  className="btn btn-ghost"
                  disabled={updateGroup.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updateGroup.isPending}
                >
                  {updateGroup.isPending ? 'Updating...' : 'Update Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeletingGroup(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Stakeholder Group"
        message={`Are you sure you want to delete "${deletingGroup?.group_name}"? This will also remove all assignments to data objects.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
};

export default StakeholderGroupsPage;
